// Single source of truth for all 6 service detail pages.
// Each entry is consumed by <ServiceDetail /> via slug lookup.

export const SERVICES_DATA = {
    fencing: {
        slug: "fencing",
        category: "Exterior · Fencing",
        seoHeading: "Fence Installation · Toronto, Brampton & the GTA",
        name: "Fencing",
        heroTitle: "Fencing that lasts decades.",
        heroSubhead:
            "Cedar, vinyl, aluminum, and chain-link fences built for GTA weather, GTA bylaws, and GTA backyards.",
        heroImage:
            "/projects/fencing/aluminum-fence-img-20250502-wa0056.webp",
        intro:
            "From classic cedar privacy fences to pool-code aluminum, we install fences that look sharp on day one and still look right ten winters later.",
        valueBullets: [
            {
                icon: "ShieldCheck",
                title: "Bylaw-compliant heights",
                body: "We know GTA municipal codes — front, side, corner-lot, pool — so your fence passes the first inspection.",
            },
            {
                icon: "Hammer",
                title: "Frost-protected posts",
                body: "Posts set below the frost line and packed correctly so heaving doesn't lift your fence in year two.",
            },
            {
                icon: "Award",
                title: "Lifetime cedar hardware",
                body: "Hot-dipped galvanized fasteners on every cedar build — no rust streaks down your boards.",
            },
            {
                icon: "Clock",
                title: "2-year workmanship warranty",
                body: "If it racks, sags, or loosens within 24 months, we fix it. No paperwork shuffle.",
            },
        ],
        materials: [
            {
                name: "Cedar",
                desc: "Western Red Cedar or pressure-treated pine. Natural look, ages gracefully.",
                image:
                    "/images/residential/fencing-m1.594cee10.webp",
                alt: "Horizontal cedar board privacy fence with black steel posts running along a residential backyard lawn",
            },
            {
                name: "Vinyl",
                desc: "White, beige, or wood-grain panels — zero-maintenance privacy.",
                image:
                    "/images/residential/fencing-m2.a6c0a4ac.webp",
                alt: "Tan vinyl privacy fence panels wrapping a side yard beside a new stamped concrete patio",
            },
            {
                name: "Aluminum",
                desc: "Powder-coated, pool-code compliant — open views, no warping.",
                image:
                    "/images/residential/fencing-m3.d62f0e7f.webp",
                alt: "Black powder-coated aluminum pool fence with open sightlines and a self-closing gate latch",
            },
            {
                name: "Chain Link",
                desc: "Galvanized or black PVC-coated — the budget-friendly classic.",
                image:
                    "/images/residential/fencing-m4.4ceaa5e8.webp",
                alt: "Black chain-link fence with privacy mesh bordering a landscaped backyard and flagstone patio at dusk",
            },
        ],
        processSteps: [
            { num: "01", title: "Free On-Site Quote", body: "We measure, check bylaws and lot lines, pick materials." },
            { num: "02", title: "Detailed Proposal", body: "Materials, gates, post count, and a fixed price." },
            { num: "03", title: "Build Days", body: "Crew arrives on time, posts set, boards up — clean as we go." },
            { num: "04", title: "Final Walk-Through", body: "We walk every panel and gate latch with you before sign-off." },
        ],
        faqs: [
            {
                q: "How tall can my fence be in Brampton or Mississauga?",
                a: "Brampton and Mississauga both allow 2.0 m (about 6'6\") in rear and side yards. Front yards are 1.0 m in Brampton; in Mississauga it's 1.0 m solid or 1.5 m see-through within 4.5 m of the street. Corner lots are stricter. Pool enclosures are a municipal rule, not provincial — both cities require at least 1.2 m and a permit. We confirm the rules for your exact address before quoting.",
            },
            {
                q: "Do I need a permit?",
                a: "Standard residential fences under code-height usually do not require a permit, but pool enclosures, retaining-wall fences, and any over-height fence do. We handle the application if needed.",
            },
            {
                q: "How long does a cedar fence last in the GTA?",
                a: "A properly built cedar fence with hot-dipped galvanized fasteners and posts below the frost line typically lasts 20–25 years. Pressure-treated pine usually 15–20. Vinyl and aluminum will outlast both with the right install.",
            },
            {
                q: "What's the cost per linear foot?",
                a: "Wide ranges depending on material, height, and gates: cedar typically $55–$95/ft installed, vinyl $80–$130/ft, aluminum $95–$160/ft, chain link $35–$65/ft. We give a fixed, line-item quote — no hourly surprises.",
            },
            {
                q: "Do you remove the old fence?",
                a: "Yes — old fence removal and disposal is always quoted as a line item up front. We don't leave debris.",
            },
        ],
    },

    interlocking: {
        slug: "interlocking",
        category: "Exterior · Interlocking & Paving",
        seoHeading: "Interlocking Driveways, Patios & Walkways · GTA",
        name: "Interlocking & Paving",
        heroTitle: "Interlocking driveways & walkways.",
        heroSubhead:
            "Driveways, walkways, and patios across Brampton & the GTA that elevate your curb appeal — engineered to outlast our winters.",
        heroImage:
            "/projects/interlocking/interlocking-20241123-125714.webp",
        intro:
            "An interlock job is only as good as its base. From our home base in North York, we install across the GTA — and we over-build the foundation so your driveway still sits flat after ten Canadian winters.",
        valueBullets: [
            {
                icon: "Hammer",
                title: "6+ inches of compacted base",
                body: "3/4\" clear stone and granular A, compacted in lifts — not a shortcut layer of sand on dirt.",
            },
            {
                icon: "ShieldCheck",
                title: "Polymeric jointing sand",
                body: "Locks pavers together, blocks weeds, and stays put in heavy rain.",
            },
            {
                icon: "Award",
                title: "Steel edge restraints",
                body: "Stops perimeter pavers from drifting — the most common failure point we see on competitor jobs.",
            },
            {
                icon: "Clock",
                title: "Lifetime base-prep warranty",
                body: "If the base settles unevenly, we re-lift it. Period.",
            },
        ],
        materials: [
            {
                name: "Concrete Pavers",
                desc: "Unilock, Techo-Bloc, Permacon — premium product lines, 100+ pattern combos.",
                image:
                    "/images/residential/interlocking-m1.eec29814.webp",
                alt: "Sunlit patio of large-format cream concrete pavers with a charcoal soldier-course border",
            },
            {
                name: "Natural Stone",
                desc: "Flagstone, limestone, granite — premium finishes with one-of-a-kind variation.",
                image:
                    "/images/residential/interlocking-m2.3934b85a.webp",
                alt: "Rain-wet natural cleft flagstone patio field framed by charcoal and tan paver borders",
            },
            {
                name: "Permeable Interlock",
                desc: "Drains stormwater on-site — great for tight lot-coverage rules.",
                image:
                    "/images/residential/interlocking-m3.ee841fb3.webp",
                alt: "Permeable paver driveway with aggregate-filled joints draining rainwater on site",
            },
            {
                name: "Edge Restraints & Sand",
                desc: "Steel/aluminum edging + polymeric sand — the details that make it last.",
                image:
                    "/images/residential/interlocking-m4.4a6e8b69.webp",
                alt: "Aluminum paver edge restraint and polymeric sand being swept into paver joints",
            },
        ],
        processSteps: [
            { num: "01", title: "Site Survey", body: "Measure, mark utility locates, plan drainage." },
            { num: "02", title: "Excavation & Base", body: "Dig down 8–10\", lay geotextile, compact base in lifts." },
            { num: "03", title: "Laying Pavers", body: "Bond pattern set, cut to spec, edge restraints installed." },
            { num: "04", title: "Finishing", body: "Polymeric sand swept, vibrated, washed in. Sealed on request." },
        ],
        faqs: [
            {
                q: "How much does an interlocking driveway cost in Brampton?",
                a: "In 2026, standard-paver driveways in Brampton and the GTA run $24–$32 per square foot installed; premium large-format pavers run $32–$45. A typical 600 sq ft double driveway lands roughly $14,000–$19,000 all-in. Quotes below about $20 per square foot almost always mean a shortcut base that settles within a few winters.",
            },
            {
                q: "Do I need a permit for an interlocking driveway in Brampton?",
                a: "Replacing an existing driveway surface like-for-like generally doesn't require a permit. Widening the driveway, changing the curb cut, or altering drainage usually does need City of Brampton approval, and corner lots have sight-line rules. We confirm the requirements with the city as part of our site survey.",
            },
            {
                q: "How long does interlock last vs. asphalt?",
                a: "A properly installed interlock driveway lasts 25–30 years with minor re-sanding. Asphalt typically needs replacement around 15–20 years and sealcoating every 2–3. Interlock costs more up front but usually wins on total cost of ownership.",
            },
            {
                q: "Should I seal the interlock?",
                a: "Sealing is optional but extends colour life and protects against oil staining. We recommend sealing 6–12 months after install (lets it cure), then every 3–5 years.",
            },
            {
                q: "Will weeds grow between the pavers?",
                a: "With polymeric sand correctly applied and re-topped every 5–7 years, weed growth is minimal. Weeds usually only return when joints aren't refreshed.",
            },
            {
                q: "Can tree roots damage interlock?",
                a: "Yes, large roots can lift pavers over time. We assess nearby trees during the survey and recommend root barriers or re-routing when needed.",
            },
            {
                q: "Can you overlay interlock on an existing driveway?",
                a: "Almost never. A proper interlock install needs a full base build. Overlaying on asphalt or old concrete leads to settling within a year or two.",
            },
        ],
    },

    decking: {
        slug: "decking",
        category: "Exterior · Decking",
        seoHeading: "Custom Deck Builders · Toronto & the GTA",
        name: "Decking",
        heroTitle: "Custom decks for outdoor living.",
        heroSubhead:
            "Composite and pressure-treated decks designed and built to last 25+ Canadian seasons.",
        heroImage:
            "/projects/decking/carport-carporteg2.webp",
        intro:
            "Whether it's a low-profile lounge deck or a multi-level entertainer's platform with built-in seating, we design and build to the way you actually live outside.",
        valueBullets: [
            {
                icon: "Hammer",
                title: "Engineered concrete footings",
                body: "Below frost line, sized to deck load — no bouncy decks.",
            },
            {
                icon: "ShieldCheck",
                title: "Code-compliant railings",
                body: "Heights, baluster spacing, stair geometry all hit Ontario Building Code.",
            },
            {
                icon: "Award",
                title: "Hidden fasteners",
                body: "On composite builds — no screw heads in your sightline.",
            },
            {
                icon: "Clock",
                title: "Permit handling included",
                body: "We pull and manage the building permit when required.",
            },
        ],
        materials: [
            {
                name: "Composite",
                desc: "Trex, TimberTech, Fiberon — minimal maintenance, 25-year warranties.",
                image:
                    "/images/residential/decking-m1.d6675bd6.webp",
                alt: "Grey capped-composite deck boards with a black aluminum railing, sunlit, beside a new wood privacy fence.",
            },
            {
                name: "Pressure-Treated Pine",
                desc: "The classic budget build — 15–20 year life with care.",
                image:
                    "/images/residential/decking-m2.1dfb5708.webp",
                alt: "Newly built pressure-treated pine deck with a matching square-baluster wood railing and solid privacy wall in a fenced backyard.",
            },
            {
                name: "Cedar",
                desc: "Natural beauty, hand-feel that no composite can match.",
                image:
                    "/images/residential/decking-m3.9b1df8da.webp",
                alt: "Warm reddish-brown natural wood deck boards with visible knots and grain, wood privacy screen, evening light.",
            },
            {
                name: "Railings & Rails",
                desc: "Aluminum, glass panels, cable rail — pick the look that fits your home.",
                image:
                    "/images/residential/decking-m4.322d60b5.webp",
                alt: "Frameless glass railing on polished stainless spigots edging a raised deck, carried down wide cascading stairs with black riser lights.",
            },
        ],
        processSteps: [
            { num: "01", title: "Design Visit", body: "Sketch the layout, talk materials, talk railings." },
            { num: "02", title: "Permit + Quote", body: "We pull the permit (if needed) and lock in a fixed price." },
            { num: "03", title: "Footings & Frame", body: "Concrete sonotubes, ledger flashing, joists." },
            { num: "04", title: "Decking & Rails", body: "Boards down, rails up, final inspection." },
        ],
        faqs: [
            {
                q: "Do I need a building permit?",
                a: "In most GTA municipalities a permit is required for decks over 24\" off the ground, attached to the house, or with structural railings. We assess and pull the permit on your behalf when needed.",
            },
            {
                q: "Composite vs. wood — which gives better ROI?",
                a: "Composite costs roughly 1.5–2x more up front but eliminates staining/sealing labour. Over 15 years, composite usually wins on total cost. For homes you plan to sell soon, pressure-treated can make sense.",
            },
            {
                q: "Can you build a deck around a pool?",
                a: "Yes — including pool-code self-latching gates and slip-resistant surfaces. Inground pools usually require engineered footings to keep loads off the pool wall.",
            },
            {
                q: "What about deck height and stair codes?",
                a: "Railings required at any deck surface > 24\" above grade (typically 36\"–42\" guard height). Stair rises 5–7\", treads 10\" min. We build to the current Ontario Building Code on every job.",
            },
            {
                q: "Can you add a roof, pergola, or gazebo?",
                a: "Yes. We do covered decks, louvered pergolas, integrated lighting, and built-in benches. Tell us how you want to use the space.",
            },
        ],
    },

    concrete: {
        slug: "concrete",
        category: "Exterior · Concrete & Asphalt",
        seoHeading: "Concrete & Asphalt Driveway Contractors · GTA",
        name: "Concrete & Asphalt",
        heroTitle: "Concrete & asphalt, done right.",
        heroSubhead:
            "Driveways, walkways, structural slabs, and asphalt paving — poured, finished, and sealed to spec.",
        heroImage:
            "/projects/concrete/asphalt-img-20260504-wa0102.webp",
        intro:
            "Concrete is a long-haul material — the difference between 30 years and 30 weeks is what happens before the truck pours. We over-engineer the prep.",
        valueBullets: [
            {
                icon: "Hammer",
                title: "4000 PSI driveway mix",
                body: "Stronger than the standard 3000 PSI most contractors pour — built for GTA freeze-thaw.",
            },
            {
                icon: "ShieldCheck",
                title: "Rebar / wire mesh reinforced",
                body: "Internal steel reinforcement on driveways and structural slabs.",
            },
            {
                icon: "Award",
                title: "Expansion joints + control cuts",
                body: "Strategically placed so cracks happen where you want them — at the joints.",
            },
            {
                icon: "Clock",
                title: "Sealed before handover",
                body: "Penetrating sealer applied on cured concrete; tack-coat & sealcoat on asphalt.",
            },
        ],
        materials: [
            {
                name: "Broom-Finish Concrete",
                desc: "Classic textured finish — slip-resistant and timeless.",
                image:
                    "/images/residential/concrete-m1.ce6c8a11.webp",
                alt: "Broom-finished concrete driveway with fine brush texture and crisp tooled control joints",
            },
            {
                name: "Stamped / Decorative",
                desc: "Pattern-stamped, integrally coloured — the look of stone at concrete cost.",
                image:
                    "/images/residential/concrete-m2.ae8ed5a9.webp",
                alt: "Stamped concrete driveway in an ashlar slate pattern, wet and glossy, running up to a two-car garage.",
            },
            {
                name: "Exposed Aggregate",
                desc: "Decorative stone showcased on the surface — premium, low-slip finish.",
                image:
                    "/images/residential/concrete-m3.160c90e6.webp",
                alt: "Exposed aggregate concrete patio with contrasting dark border bands surrounding a metal gazebo and garden shed.",
            },
            {
                name: "Asphalt Paving",
                desc: "New driveways, resurfacing, sealcoating, and pothole repair.",
                image:
                    "/images/residential/concrete-m4.72c5469c.webp",
                alt: "Freshly paved asphalt driveways meeting a new poured concrete curb along a residential street.",
            },
        ],
        processSteps: [
            { num: "01", title: "Site Prep", body: "Remove existing surface, grade to spec, install forms." },
            { num: "02", title: "Base + Steel", body: "Compacted granular base, rebar/mesh tied in." },
            { num: "03", title: "Pour & Finish", body: "Pour, screed, float, cut control joints, broom or stamp." },
            { num: "04", title: "Cure & Seal", body: "Cure 7+ days, apply penetrating sealer, hand over." },
        ],
        faqs: [
            {
                q: "Concrete vs. asphalt — which is more durable?",
                a: "Concrete typically lasts 30+ years with light maintenance. Asphalt 15–20 with sealcoating every 2–3 years. Concrete costs more up front but has a longer life and better resale appeal.",
            },
            {
                q: "How do you prevent cracking?",
                a: "We use a 4000 PSI mix, embed wire mesh or rebar, place control joints at correct intervals, and cure properly (7+ days). Hairline cracks may still appear (concrete moves) — but big structural cracks shouldn't.",
            },
            {
                q: "How soon can I drive on a new concrete driveway?",
                a: "Walk on it after 24 hours, light vehicles after 7 days, heavy vehicles after 28 days. We mark the cure window on your handover.",
            },
            {
                q: "Will salt damage my driveway?",
                a: "Yes — chloride-based de-icers attack concrete. We recommend sand or calcium magnesium acetate (CMA) in the first winter, and avoiding heavy salting long-term. Sealing helps significantly.",
            },
            {
                q: "How often should I reseal?",
                a: "Concrete: every 3–5 years with a penetrating sealer. Asphalt: every 2–3 years with sealcoat. We'll set a reminder cycle for you.",
            },
        ],
    },

    landscaping: {
        slug: "landscaping",
        category: "Exterior · Landscaping",
        seoHeading: "Landscaping & Retaining Walls · Toronto & the GTA",
        name: "Landscaping",
        heroTitle: "Full-service landscaping.",
        heroSubhead:
            "Sod, garden beds, retaining walls, and full backyard transformations across the GTA.",
        heroImage:
            "/projects/landscaping/shed-designs-woodshed1.webp",
        intro:
            "Landscaping is half horticulture, half engineering. We do both — drainage, grading, plant selection, and the kind of crisp install you actually want to look at.",
        valueBullets: [
            {
                icon: "Hammer",
                title: "Site grading included",
                body: "We slope the soil away from the house so water doesn't end up in your basement.",
            },
            {
                icon: "ShieldCheck",
                title: "Drainage assessment",
                body: "Catch-basin or French drain recommendations baked into the quote when needed.",
            },
            {
                icon: "Award",
                title: "GTA-hardy plant selection",
                body: "Native and zone-5 hardy choices — what survives a Brampton January.",
            },
            {
                icon: "Clock",
                title: "Post-install care plan",
                body: "Written watering and seasonal-prune plan for your first year.",
            },
        ],
        materials: [
            {
                name: "Sod Installation",
                desc: "Fresh-cut Kentucky bluegrass, prepped soil, full hand-roll.",
                image:
                    "/images/residential/landscaping-m1.fce14c76.webp",
                alt: "Crew hand-laying fresh sod rolls over graded soil in a fenced backyard",
            },
            {
                name: "Garden Beds & Plantings",
                desc: "Native shrubs, perennials, mulched and edged.",
                image:
                    "/images/residential/landscaping-m2.9933ef67.webp",
                alt: "Long garden bed topped with fresh dark soil, stone edging, and newly planted cedars",
            },
            {
                name: "Retaining Walls",
                desc: "Allan Block, natural stone, gabion — engineered for your slope.",
                image:
                    "/images/residential/landscaping-m3.9b886f10.webp",
                alt: "Completed timber retaining wall holding a sloped yard beside a chain-link fence and trees",
            },
            {
                name: "Drainage & Grading",
                desc: "French drains, catch basins, swales — solve the water problem first.",
                image:
                    "/images/residential/landscaping-m4.77726a59.webp",
                alt: "Black corrugated perforated drain pipe and filter fabric laid in an excavated strip beside a low timber garden wall",
            },
        ],
        processSteps: [
            { num: "01", title: "Yard Walk", body: "Walk the property, identify drainage issues, capture your wishlist." },
            { num: "02", title: "Plan + Quote", body: "Sketch, plant list, materials, and a fixed price." },
            { num: "03", title: "Hardscape First", body: "Grading, drainage, walls, edging — the bones." },
            { num: "04", title: "Plantings & Sod", body: "Plant, sod, mulch, and a written care plan." },
        ],
        faqs: [
            {
                q: "When is the best time to lay sod in the GTA?",
                a: "Mid-April through early June, then late August through October. Mid-summer sod takes constant watering and is harder to establish.",
            },
            {
                q: "How much should I water new sod?",
                a: "Heavy daily watering for the first 14 days (twice a day in hot weather), then taper down. We hand you a printed schedule on install day.",
            },
            {
                q: "Do I need a permit for a retaining wall?",
                a: "Walls over 1.0 m (3'3\") in Ontario typically require a permit and engineered drawings. We handle both — engineering and the permit.",
            },
            {
                q: "What's the cost range for a full backyard makeover?",
                a: "Highly variable depending on hardscape. A simple sod + garden bed refresh is often $5K–$10K; a full landscape with retaining wall, patio, and plantings can run $30K–$80K+. We give a fixed quote after the site walk.",
            },
            {
                q: "Do you handle drainage issues — wet basements, pooling water?",
                a: "Yes. Drainage is usually the first thing we assess. Common fixes: regrading, French drains, downspout extensions, catch basins. We don't just plant over a problem.",
            },
        ],
    },

    renovations: {
        slug: "renovations",
        category: "Interior · Renovations",
        seoHeading: "Kitchen, Bath & Basement Renovations · GTA",
        name: "Interior Renovations",
        heroTitle: "Interiors finished to impress.",
        heroSubhead:
            "Kitchens, bathrooms, basements, and epoxy floors — full-service interior renovations with fixed quotes.",
        heroImage:
            "/projects/renovations/bathrooms-img-20251024-wa0027.webp",
        intro:
            "One contract, one crew, one timeline — no juggling six trades or chasing missing tile installers. We project-manage start to finish.",
        valueBullets: [
            {
                icon: "Hammer",
                title: "Fixed quotes",
                body: "Line-itemed, no T&M surprise invoices at the end.",
            },
            {
                icon: "ShieldCheck",
                title: "Licensed trades only",
                body: "Licensed electricians and plumbers on every job — no homeowner-grade shortcuts.",
            },
            {
                icon: "Award",
                title: "Single point of contact",
                body: "One project manager from kickoff to final punch list.",
            },
            {
                icon: "Clock",
                title: "Permit handling included",
                body: "We pull and close the permit — you don't talk to the municipality.",
            },
        ],
        materials: [
            {
                name: "Kitchen Renovations",
                desc: "Cabinets, countertops, tile, plumbing fixtures, lighting — full or partial.",
                image:
                    "/images/residential/renovations-m1.d4d4675f.webp",
                alt: "Finished kitchen with gloss grey cabinets, white quartz island, marble-look backsplash and large tile flooring.",
            },
            {
                name: "Bathroom Renovations",
                desc: "From a refreshed vanity to a full tile-shower rebuild.",
                image:
                    "/images/residential/renovations-m2.f3ab52e3.webp",
                alt: "Renovated bathroom with floating wood vanity, framed mirror, marble-tiled walk-in shower and polished porcelain floor.",
            },
            {
                name: "Basement Finishing",
                desc: "Framing, drywall, electrical, flooring, bathroom rough-ins.",
                image:
                    "/images/residential/renovations-m3.cf28591b.webp",
                alt: "Finished basement with painted walls, pot lights, tiled floor and a full kitchen along one wall.",
            },
            {
                name: "Epoxy & Tile Floors",
                desc: "Garage, basement, laundry — durable, easy-clean, premium finish.",
                image:
                    "/images/residential/renovations-m4.9db53da6.webp",
                alt: "Glossy grey epoxy garage floor with decorative flake and a clean coved wall edge",
            },
        ],
        processSteps: [
            { num: "01", title: "In-Home Consult", body: "Measure, talk scope, materials, budget range." },
            { num: "02", title: "Fixed Quote + Schedule", body: "Locked price, locked dates, locked deliverables." },
            { num: "03", title: "Build", body: "Demo, rough-ins, finishes — managed by your project lead." },
            { num: "04", title: "Punch List", body: "Walk every detail with you, close permit, hand over keys." },
        ],
        faqs: [
            {
                q: "How long does a typical kitchen reno take?",
                a: "A standard 100–150 sq ft kitchen reno runs 4–6 weeks from demo to final clean, longer with structural changes or custom cabinets (lead time 8–10 weeks). We give you a Gantt chart at quote.",
            },
            {
                q: "Do I need a permit for a basement renovation?",
                a: "Yes — finishing a basement (new framed walls, electrical, often new plumbing) requires a building permit in every GTA municipality. We pull and manage it.",
            },
            {
                q: "What's the typical cost range?",
                a: "Bathroom refreshes from $12K, full bathrooms $20–$40K. Kitchens $25–$80K+ depending on cabinet line and counters. Basement finishing $40–$80K for a typical layout. Fixed quote after the consult.",
            },
            {
                q: "Can I live in the home during the work?",
                a: "For bathroom/basement work — usually yes. For full kitchen renos, most clients set up a temporary kitchen and stay. We seal off the work area, run negative-air on dusty days, and clean each evening.",
            },
            {
                q: "What's the warranty?",
                a: "2-year workmanship warranty on all interior renovations, plus manufacturer warranties on materials and fixtures (typically 5–25 years).",
            },
        ],
    },
};

export const SERVICES_LIST = Object.values(SERVICES_DATA);
